import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Avatar from "./component/Avatar";

function AllUserTable({ onRowClick }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState("");

  const PER = 15;

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();

  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("adminToken");

      const response = await fetch(
        "https://consult.conversationaily.ai/api/superadmin/students",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      setUsers(data?.students || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setLoadingOrganizations(true);

      const token = localStorage.getItem("adminToken");

      const response = await fetch(
        "https://testone.conversationaily.ai/api/organizations",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      setOrganizations(data?.organizations || data || []);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !q ||
        `${user.first_name || ""} ${user.last_name || ""}`
          .toLowerCase()
          .includes(q) ||
        user.email?.toLowerCase().includes(q);

      const matchesOrganization =
        !selectedOrganization ||
        user.organization === selectedOrganization;

      return matchesSearch && matchesOrganization;
    });
  }, [users, search, selectedOrganization]);

  const pages = Math.ceil(filtered.length / PER);
  const visible = filtered.slice(page * PER, (page + 1) * PER);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-6 text-center">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
          />

          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>

        <div className="w-full md:w-72">
          <select
            value={selectedOrganization}
            onChange={(e) => {
              setSelectedOrganization(e.target.value);
              setPage(0);
            }}
            disabled={loadingOrganizations}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            <option value="">All Organizations</option>

            {organizations.map((org) => (
              <option key={org.id} value={org.code}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                {[
                  "Name",
                  "Email",
                  "Organization",
                  "Role",
                  "Status",
                  "Created",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {visible.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => onRowClick?.(user)}
                  className="hover:bg-orange-50/50 cursor-pointer transition-colors group"
                >
                  {/* Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        firstName={user.first_name}
                        lastName={user.last_name}
                      />

                      <span className="font-semibold text-gray-800 whitespace-nowrap">
                        {user.first_name} {user.last_name}
                      </span>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {user.email || "—"}
                  </td>

                  {/* Organization */}
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1 text-xs font-medium">
                      {organizations.find((org) => org.code === user.organization)?.name || user.organization}
                    </span>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${user.role?.toLowerCase() === "admin"
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : user.role?.toLowerCase() === "superadmin"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                        }`}
                    >
                      {user.role || "User"}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${user.status === "Approved"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : user.status === "Rejected"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : user.status === "Pending"
                            ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                            : "bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                    >
                      <span
                        className={`mr-2 h-2 w-2 rounded-full ${user.status === "Approved"
                          ? "bg-green-500"
                          : user.status === "Rejected"
                            ? "bg-red-500"
                            : user.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-gray-500"
                          }`}
                      />
                      {user.status || "Pending"}
                    </span>
                  </td>

                  {/* Created */}
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : "—"}
                  </td>

                </tr>
              ))}

              {!visible.length && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-8 text-gray-400"
                  >
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span>
            {filtered.length
              ? `${page * PER + 1}-${Math.min(
                (page + 1) * PER,
                filtered.length
              )} of ${filtered.length}`
              : "0 users"}
          </span>

          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              ← Prev
            </button>

            <span className="px-2 py-1.5">
              {pages ? page + 1 : 0}/{pages || 0}
            </span>

            <button
              onClick={() =>
                setPage((p) => Math.min(pages - 1, p + 1))
              }
              disabled={page >= pages - 1 || pages === 0}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllUserTable;